import { CartAddEvent } from '@theme/events';

class PwctBundleBuilder extends HTMLElement {
  /** @type {Set<string>} */
  #cartVariantIds = new Set();

  /** @type {WeakMap<HTMLElement, { timer: number, cards: HTMLElement[] }>} */
  #pageMotions = new WeakMap();

  connectedCallback() {
    this.#cartVariantIds = new Set(
      (this.dataset.cartVariantIds || '')
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
    );

    this.addEventListener('click', this.#handleClick);
    this.addEventListener('change', this.#handleChange);
    document.addEventListener('cart:update', this.#handleCartUpdate);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('change', this.#handleChange);
    document.removeEventListener('cart:update', this.#handleCartUpdate);
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const prev = target.closest('[data-prev]');
    const next = target.closest('[data-next]');
    if (prev || next) {
      const tier = target.closest('[data-tier]');
      if (!(tier instanceof HTMLElement)) return;
      const page = Number(tier.dataset.page || 0);
      const pages = Number(tier.dataset.pages || 1);
      const nextPage = prev ? Math.max(0, page - 1) : Math.min(pages - 1, page + 1);
      if (nextPage === page) return;
      this.#finishPageMotion(tier);
      tier.dataset.page = String(nextPage);
      tier.dataset.pageDirection = prev ? 'prev' : 'next';
      this.#showPage(tier, prev ? -1 : 1);
      return;
    }

    const button = target.closest('[data-add]');
    if (!(button instanceof HTMLButtonElement) || button.disabled || button.dataset.loading === 'true') return;
    this.#add(button);
  };

  /** @param {Event} event */
  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLSelectElement) || !target.hasAttribute('data-variant-select')) return;
    const card = target.closest('[data-bundle-card]');
    if (card instanceof HTMLElement) this.#syncCard(card);
  };

  /** @param {Event} event */
  #handleCartUpdate = (event) => {
    const items = event instanceof CustomEvent ? event.detail?.resource?.items : null;
    if (!Array.isArray(items)) return;
    this.#cartVariantIds = new Set(items.map((item) => String(item.variant_id)));
    this.querySelectorAll('[data-bundle-card]').forEach((card) => {
      if (card instanceof HTMLElement) this.#syncCard(card);
    });
  };

  /**
   * @param {HTMLElement} tier
   * @param {number} [direction]
   */
  #showPage(tier, direction = 0) {
    const cards = this.#tierCards(tier);
    const { start, end } = this.#pageRange(tier);
    const animation = this.dataset.pageAnimation || 'none';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const outgoing = cards.filter((card) => !card.hidden && !this.#cardOnPage(card, start, end));
    const incoming = cards.filter((card) => card.hidden && this.#cardOnPage(card, start, end));
    const canAnimate = direction !== 0 && animation !== 'none' && !reduceMotion && (outgoing.length > 0 || incoming.length > 0);

    if (!canAnimate) {
      this.#applyPage(tier);
      return;
    }

    const list = tier.querySelector('.pwct-bundle-builder__list');
    if (animation === 'slide' && list instanceof HTMLElement) {
      this.#slidePages(list, outgoing, incoming);
    } else if (list instanceof HTMLElement) {
      const listRect = list.getBoundingClientRect();
      outgoing.forEach((card) => {
        const rect = card.getBoundingClientRect();
        card.style.position = 'absolute';
        card.style.top = `${rect.top - listRect.top}px`;
        card.style.left = `${rect.left - listRect.left}px`;
        card.style.width = `${rect.width}px`;
        card.style.zIndex = '1';
        card.classList.add('is-page-leave');
      });
      incoming.forEach((card) => {
        card.hidden = false;
        card.classList.add('is-page-enter');
      });
    }

    const active = document.activeElement;
    if (active instanceof HTMLElement && outgoing.some((card) => card.contains(active))) {
      const visible = tier.querySelector('[data-bundle-card]:not(.is-page-leave):not([hidden]) a, [data-bundle-card]:not(.is-page-leave):not([hidden]) button, [data-bundle-card]:not(.is-page-leave):not([hidden]) select');
      if (visible instanceof HTMLElement) visible.focus();
    }

    const duration = Number.parseFloat(getComputedStyle(this).getPropertyValue('--pwct-bb-page-duration')) || 300;
    const timer = window.setTimeout(() => this.#finishPageMotion(tier), duration + 40);
    this.#pageMotions.set(tier, { timer, cards: [...outgoing, ...incoming] });
    this.#updatePager(tier);
  }

  /**
   * @param {HTMLElement} list
   * @param {HTMLElement[]} outgoing
   * @param {HTMLElement[]} incoming
   */
  #slidePages(list, outgoing, incoming) {
    const width = list.clientWidth;
    const outgoingHeight = list.offsetHeight;
    const listTop = list.getBoundingClientRect().top;

    /** @param {HTMLElement} card @param {number} top @param {number} height */
    const place = (card, top, height) => {
      card.style.position = 'absolute';
      card.style.top = `${top}px`;
      card.style.bottom = 'auto';
      card.style.left = '0';
      card.style.width = `${width}px`;
      card.style.height = `${height}px`;
      card.style.margin = '0';
      card.style.alignSelf = 'flex-start';
    };

    const outgoingLayout = outgoing.map((card) => {
      const rect = card.getBoundingClientRect();
      return { top: rect.top - listTop, height: rect.height };
    });

    list.style.height = `${outgoingHeight}px`;
    outgoing.forEach((card, index) => {
      place(card, outgoingLayout[index].top, outgoingLayout[index].height);
    });

    incoming.forEach((card) => {
      card.hidden = false;
    });

    const incomingLayout = incoming.map((card) => {
      const rect = card.getBoundingClientRect();
      return { top: rect.top - list.getBoundingClientRect().top, height: rect.height };
    });

    incoming.forEach((card, index) => {
      place(card, incomingLayout[index].top, incomingLayout[index].height);
      card.classList.add('is-page-enter');
    });
    outgoing.forEach((card) => card.classList.add('is-page-leave'));

    const incomingHeight = incomingLayout.length
      ? incomingLayout[incomingLayout.length - 1].top + incomingLayout[incomingLayout.length - 1].height
      : 0;

    list.classList.add('is-page-sliding');
    list.style.height = `${Math.max(outgoingHeight, incomingHeight)}px`;
  }

  /** @param {HTMLElement} tier */
  #finishPageMotion(tier) {
    const motion = this.#pageMotions.get(tier);
    if (!motion) return;
    window.clearTimeout(motion.timer);
    this.#pageMotions.delete(tier);
    this.#applyPage(tier);
  }

  /** @param {HTMLElement} tier */
  #applyPage(tier) {
    const { start, end } = this.#pageRange(tier);
    const list = tier.querySelector('.pwct-bundle-builder__list');
    if (list instanceof HTMLElement) {
      list.classList.remove('is-page-sliding');
      list.style.height = '';
      list.style.overflow = '';
    }
    this.#tierCards(tier).forEach((card) => {
      card.classList.remove('is-page-leave', 'is-page-enter');
      card.style.position = '';
      card.style.top = '';
      card.style.left = '';
      card.style.width = '';
      card.style.height = '';
      card.style.bottom = '';
      card.style.margin = '';
      card.style.alignSelf = '';
      card.style.zIndex = '';
      card.hidden = !this.#cardOnPage(card, start, end);
    });
    this.#updatePager(tier);
  }

  /** @param {HTMLElement} tier */
  #pageRange(tier) {
    const perPage = Number(tier.dataset.perPage || 3);
    const page = Number(tier.dataset.page || 0);
    return { start: page * perPage, end: page * perPage + perPage };
  }

  /** @param {HTMLElement} tier */
  #tierCards(tier) {
    return [...tier.querySelectorAll('[data-bundle-card]')].filter((card) => card instanceof HTMLElement);
  }

  /**
   * @param {HTMLElement} card
   * @param {number} start
   * @param {number} end
   */
  #cardOnPage(card, start, end) {
    const index = Number(card.dataset.cardIndex || 0);
    return index >= start && index < end;
  }

  /** @param {HTMLElement} tier */
  #updatePager(tier) {
    const page = Number(tier.dataset.page || 0);
    const pages = Number(tier.dataset.pages || 1);
    const prev = tier.querySelector('[data-prev]');
    const next = tier.querySelector('[data-next]');
    if (prev instanceof HTMLButtonElement) prev.disabled = page <= 0;
    if (next instanceof HTMLButtonElement) next.disabled = page >= pages - 1;

    const active = document.activeElement;
    if (active instanceof HTMLElement && tier.contains(active) && active.closest('[data-bundle-card]')?.hidden) {
      const visible = tier.querySelector('[data-bundle-card]:not([hidden]) a, [data-bundle-card]:not([hidden]) button, [data-bundle-card]:not([hidden]) select');
      if (visible instanceof HTMLElement) visible.focus();
    }
  }

  /** @param {HTMLElement} card */
  #syncCard(card) {
    const select = card.querySelector('[data-variant-select]');
    const option = select instanceof HTMLSelectElement ? select.selectedOptions[0] : null;
    const price = card.querySelector('[data-price]');
    const compare = card.querySelector('[data-compare]');
    const compareValue = card.querySelector('[data-compare-value]');
    const button = card.querySelector('[data-add]');
    const label = button?.querySelector('[data-add-label]');
    const inCart = card.querySelector('[data-in-cart]');
    const comingSoon = card.dataset.comingSoon === 'true';
    const variantId = option?.value || card.dataset.variantId || '';
    const available = option ? option.dataset.available === 'true' : card.dataset.available === 'true';

    if (variantId) card.dataset.variantId = variantId;
    if (price && option?.dataset.price) price.textContent = option.dataset.price;

    if (compare instanceof HTMLElement && option) {
      const comparePrice = option.dataset.compare || '';
      compare.hidden = comparePrice === '';
      if (compareValue) compareValue.textContent = comparePrice;
    }

    const added = variantId !== '' && this.#cartVariantIds.has(String(variantId));
    if (inCart instanceof HTMLElement) inCart.hidden = !added;

    if (button instanceof HTMLButtonElement) {
      const quantity = option?.dataset.quantity || button.dataset.quantity || '1';
      button.dataset.quantity = quantity;
      const loading = button.dataset.loading === 'true';
      const unavailable = !comingSoon && !available;
      button.classList.toggle('is-added', added && !unavailable);
      button.classList.toggle('is-unavailable', unavailable);
      if (!added) button.classList.remove('is-confirmed');
      button.setAttribute('aria-pressed', added ? 'true' : 'false');
      let pressedLabel = button.dataset.labelAdd || '';
      if (added) pressedLabel = card.dataset.addedMessage || pressedLabel;
      else if (comingSoon) pressedLabel = this.dataset.labelComingSoon || pressedLabel;
      else if (unavailable) pressedLabel = this.dataset.labelSoldOut || pressedLabel;
      if (pressedLabel) button.setAttribute('aria-label', pressedLabel);
      button.disabled = loading || comingSoon || !available;
      if (label && !loading) {
        if (comingSoon) label.textContent = this.dataset.labelComingSoon || '';
        else label.textContent = this.dataset.labelAdd || '';
      }
    }
  }

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    const card = button.closest('[data-bundle-card]');
    if (!(card instanceof HTMLElement)) return;

    const variantId = card.dataset.variantId;
    if (!variantId) return;

    const error = card.querySelector('[data-error]');
    const liveRegion = this.querySelector('[data-live-region]');

    button.dataset.loading = 'true';
    button.dataset.confirm = 'false';
    button.classList.remove('is-confirmed');
    button.disabled = true;
    if (error instanceof HTMLElement) {
      error.hidden = true;
      error.textContent = '';
    }

    const sectionIds = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: Number(button.dataset.quantity || 1),
            },
          ],
          sections: sectionIds.join(','),
        }),
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        const message = data.description || data.message || this.dataset.labelError || '';
        if (error instanceof HTMLElement) {
          error.hidden = false;
          error.textContent = message;
        }
        if (liveRegion) liveRegion.textContent = message;
        return;
      }

      this.#cartVariantIds.add(String(variantId));

      let cart = { item_count: this.#cartVariantIds.size };
      try {
        const cartResponse = await fetch('/cart.js');
        const nextCart = await cartResponse.json();
        if (Array.isArray(nextCart.items)) {
          cart = nextCart;
          this.#cartVariantIds = new Set(nextCart.items.map((item) => String(item.variant_id)));
        }
      } catch {
        cart = { item_count: this.#cartVariantIds.size };
      }

      button.dataset.confirm = 'true';
      if (liveRegion) liveRegion.textContent = card.dataset.addedMessage || Theme.translations?.added || '';

      this.dispatchEvent(
        new CartAddEvent(cart, variantId, {
          source: 'pwct-bundle-builder',
          itemCount: Number(cart.item_count) || 1,
          productId: card.dataset.productId,
          sections: data.sections,
        })
      );
    } catch (error) {
      const message = this.dataset.labelError || '';
      const errorEl = card.querySelector('[data-error]');
      if (errorEl instanceof HTMLElement) {
        errorEl.hidden = false;
        errorEl.textContent = message;
      }
      if (liveRegion) liveRegion.textContent = message;
    } finally {
      const confirm = button.dataset.confirm === 'true';
      button.dataset.loading = 'false';
      button.dataset.confirm = 'false';
      this.#syncCard(card);
      if (confirm) requestAnimationFrame(() => button.classList.add('is-confirmed'));
    }
  }
}

if (!customElements.get('pwct-bundle-builder')) {
  customElements.define('pwct-bundle-builder', PwctBundleBuilder);
}
